import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import { StorefrontOutlined } from '@mui/icons-material';
import axios from 'axios';
import { ENDPOINTS } from '../../config';

const BrandClaimLanding = () => {
  const { brandId } = useParams();
  const history = useHistory();
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState(null);

  const errorRef = useRef(null);

  const searchParams = new URLSearchParams(window.location.search);
  const claimToken = searchParams.get('code');

  useEffect(() => {
    document.title = 'Claim Your Brand — Step 1 of 3 | Piatto';
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const handleInitiateClaim = async () => {
    if (!claimToken) {
      setError('Invalid claim link. Please use the link from your email.');
      return;
    }

    try {
      setInitiating(true);
      setError(null);

      const response = await axios.post(ENDPOINTS.initiateBrandClaim, {
        brandId,
        claimToken,
      });

      const { sessionId, brand } = response.data;
      history.push(`/claim-brand/${brandId}/create-account`, {
        sessionId,
        brand,
      });
    } catch (err) {
      console.error('Error initiating brand claim:', err);
      setError(
        err.response?.data?.error ||
        'Failed to initiate claim. Please try again or contact support.'
      );
    } finally {
      setInitiating(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Claim Your Brand's Venues
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Welcome to Piatto! Claim all your locations and create your management account.
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <StorefrontOutlined
              sx={{ fontSize: 40, color: 'primary.main', mr: 2 }}
              aria-hidden="true"
            />
            <Typography variant="h5" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {brandId.replace(/-/g, ' ')}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Typography variant="body1" color="text.secondary">
              This link will grant you management access to all venues under this brand.
              You'll be able to view and manage every location from a single dashboard.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              role="alert"
              ref={errorRef}
              tabIndex={-1}
            >
              {error}
            </Alert>
          )}

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            By clicking the link in our email, you've confirmed access to this
            brand's inbox — thank you. Click below to set up your Piatto account and connect
            it to all your venues.
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleInitiateClaim}
              disabled={initiating || !claimToken}
              aria-busy={initiating}
              sx={{
                py: 1.5,
                px: 6,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              {initiating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 2 }} aria-hidden="true" />
                  Starting…
                </>
              ) : (
                'Claim All Venues'
              )}
            </Button>

            {!claimToken && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
                Invalid claim link. Please use the link from your email.
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Not the right brand, or need help?{' '}
              <a href="mailto:hello@piatto.com.au" style={{ color: '#B91C1C' }}>
                Contact us at hello@piatto.com.au
              </a>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Claiming is free and takes less than 2 minutes.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Questions? Visit{' '}
          <a href="https://piatto.com.au" style={{ color: '#B91C1C' }}>
            piatto.com.au
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default BrandClaimLanding;
